import moment from "moment/moment";
import dayjs from 'dayjs';

export const currencyFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0});

export const currencyFormatterDecimal = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const preferredCountries = ["971", "966", "974", "965","968","973"];

export const cleanName = (name = "") => {
    return (name ?? "")
        .toLowerCase() // Convert the entire string to lowercase
        .replace(/[_-]+/g, " ") // Replace underscores and dashes with spaces
        .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize each word
};

export const booleanFormatter = (value, [trueLabel = "True", falseLabel = "False"] = []) => {
    // Check if the value is 0 or falsy, return the false label
    if (!value || value === 0) {
        return falseLabel;
    }
    // For truthy values, return the true label
    return trueLabel;
};

export const dateFormatter = (date, options = {}) => {

    if (!date) {
        return "-";
    }

    const defaultOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
    };

    return new Date(date).toLocaleDateString("en-US", {...defaultOptions, ...options});
}

export const timeFormatter = (time) => {
    if (!time) {
        return "-";
    }
    const date = new Date(`1970-01-01T${time}`);
    return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: true});
};

/**
 * Format date as relative time (e.g., "1 min ago", "2 hours ago") for recent dates,
 * and exact date/time for older dates
 * @param {string|Date} date - The date to format
 * @param {number} thresholdDays - Number of days after which to show exact date (default: 7)
 * @returns {object} - { display: string, tooltip: string }
 */
export const relativeTimeFormatter = (date, thresholdDays = 7) => {
    if (!date) {
        return { display: "-", tooltip: "" };
    }

    const dateMoment = moment(date);
    const now = moment();
    const daysDiff = now.diff(dateMoment, 'days');

    const exactTime = dateMoment.format("D MMM YYYY hh:mm A");

    if (daysDiff < thresholdDays) {
        return {
            display: dateMoment.fromNow(),
            tooltip: exactTime
        };
    }

    return {
        display: exactTime,
        tooltip: dateMoment.fromNow()
    };
};

/**
 * Returns an error message when roster editing should be blocked, or null if allowed.
 */
export const getRosterEditBlockReason = ({
    clickedDate,
    settings = {},
    rosters = [],
    rosterEntry = null,
    canEditLocked = false,
}) => {
    if (canEditLocked) {
        return null;
    }

    const startOfWeek = parseInt(settings['roster.roster.start_of_week'] || 1, 10);
    const editableOn = parseInt(settings['roster.roster.editable_on'] || 1, 10);
    const lockedOn = parseInt(settings['roster.roster.locked_on'] || 5, 10);
    const editableOnTime = settings['roster.roster.editable_on_time'] || '09:00';
    const lockedOnTime = settings['roster.roster.locked_on_time'] || '17:00';

    const date = dayjs(clickedDate);
    const weekBase = dayjs().startOf('week');
    const currentWeekStart = weekBase.add(startOfWeek, 'day').startOf('day');
    const currentWeekEnd = weekBase.add(startOfWeek + 6, 'day').endOf('day');

    if (date.isBefore(currentWeekStart, 'day')) {
        return 'Roster entries cannot be created for previous weeks. Only current and future week entries are allowed.';
    }

    if (date.isAfter(currentWeekEnd, 'day')) {
        if (rosterEntry?.is_locked === 1) {
            return 'This roster entry is locked and cannot be modified.';
        }
        return null;
    }

    const [editableHour, editableMinute = 0] = editableOnTime.split(':').map(Number);
    const [lockedHour, lockedMinute = 0] = lockedOnTime.split(':').map(Number);
    const editableAt = weekBase.add(editableOn, 'day').hour(editableHour).minute(editableMinute).second(0);
    const lockedAt = weekBase.add(lockedOn, 'day').hour(lockedHour).minute(lockedMinute).second(59);
    const now = dayjs();

    if (now.isBefore(editableAt)) {
        return `Roster editing opens on ${editableAt.format('ddd D MMM, h:mm A')}.`;
    }

    if (now.isAfter(lockedAt)) {
        return 'Roster editing window has closed for the current week.';
    }

    const currentWeekRoster = rosters.find((roster) =>
        dayjs(roster.start_date).isSame(currentWeekStart, 'day') &&
        roster.status?.name === 'PDF_GENERATED'
    );

    if (currentWeekRoster) {
        return 'Current week roster has been generated and is locked. No new entries can be created.';
    }

    if (rosterEntry?.is_locked === 1) {
        return 'This roster entry is locked and cannot be modified.';
    }

    return null;
};